import { useEffect, useLayoutEffect, useRef, useState } from "react"

import { Drawer, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { Navigate, useLocation, useParams } from "react-router-dom"

import { RoutesPaths } from "../../../constants/user-routes"
import { useExternalDemoMode } from "../../../hooks/useExternalDemoMode"
import { usePhoneCountries } from "../../../hooks/usePhoneCountries"
import { highAccessRoles } from "../../../Utils/permission"
import { AdminReceiptTitle } from "../components/AdminReceiptTitle/AdminReceiptTitle"
import { RECEIPT_CONFIGURE_SLUGS } from "../constants/receipt-configure-slugs"
import { SECTION_KEYS } from "../constants/steps"
import { useReceiptBusinessId } from "../hooks/useReceiptBusinessId"
import ConfigureTabs, { RECEIPT_CONFIGURE_TABS } from "./components/ConfigureTabs/ConfigureTabs"
import Footer from "./components/Footer"
import LeftRail from "./components/LeftRail"
import LivePreview from "./components/LivePreview/LivePreview"
import ProfileStatusOverlay from "./components/ProfileStatusOverlay/ProfileStatusOverlay"
import SectionForm from "./components/SectionForm"
import WizardSkeleton from "./components/WizardSkeleton/WizardSkeleton"
import { loadProfile } from "./constants/externalOps"
import {
  hydrateConfigure as hydrateConfigureAction,
  resetConfigure as resetConfigureAction
} from "./redux/configure.slice"
import { buildingBlocksFromConfigure, overlayStatusFromView, wizardView } from "./utils/hydrate"

import styles from "./shell.module.less"

const { Title, Text } = Typography

// Receipt "Set up / Edit Digital Receipt" wizard. A full page (not a modal),
// built on the shared wizard shell (rail + scrolling form pane + Save/Next
// footer). Reuses the .recover-configure / .rcv-* / .dq-* styles for a
// consistent look across configure surfaces.
const ReceiptConfigurePage = ({
  hasUnsavedEdits,
  wizard,
  isHydrated,
  baRoleId,
  hydrateConfigure,
  resetConfigure,
  usersViewForAdmin
}) => {
  const { t } = useTranslation()
  const { section } = useParams()
  const { search } = useLocation()
  const [showErrors, setShowErrors] = useState(false)
  // Mobile-only: the live preview drawer, triggered by a button in the rail.
  // Desktop keeps the aside; the drawer only mounts when isMobile is true, so
  // there's never a second copy of LivePreview mounted at the same time.
  const [previewOpen, setPreviewOpen] = useState(false)
  const isMobile = useSelector((s) => !!s.nav?.isMobile)
  // True when the Configure read failed. Kept apart from "hydrated" so a
  // failure shows an error rather than an empty first-onboarding form the
  // merchant might start filling in over real saved data.
  const [loadFailed, setLoadFailed] = useState(false)
  // Identifies the newest in-flight Configure request; see the hydration effect.
  const requestSequence = useRef(0)
  // Warm the phone-countries cache so the Support step's PhoneInput shows flags.
  usePhoneCountries()
  // The form pane scrolls independently of the page; hold a ref so each step
  // change can reset it to the top (see the validSection effect below).
  const scrollRef = useRef(null)

  // Demo / External Demo (§18.2): Configure is disabled everywhere in both.
  const isDemoAccount = useSelector((s) => !!s.auth?.userInfo?.is_demo)

  const isExternalDemo = useExternalDemoMode()
  const isDemoMode = isDemoAccount || isExternalDemo

  // Support reads another merchant's profile; a merchant reads their own.
  const targetBaId = useReceiptBusinessId(usersViewForAdmin)
  // Declared once because three of this page's four returns render it.
  const adminTitle = <AdminReceiptTitle usersViewForAdmin={usersViewForAdmin} />

  // Everything about wizard mode comes from the server's own wizard block.
  // There is deliberately no local "setup complete" boolean any more: one
  // derived from the form values said Done while the server said nothing had
  // been submitted.
  const view = wizardView(wizard)
  // Receipt content edit is Administrator & Owner only; every other BA role is
  // view-only (§11.15). Mirrors the isHighAccessRoleBA check in
  // Sidebar/InternalPages.
  const canEdit = !baRoleId || highAccessRoles.includes(baRoleId)
  // anything but `null` disables the form and renders the blurred overlay over
  // the main pane (replaces UnderReviewBanner + ConfigureSummary — CHA1-2083 /
  // CHA1-2085).
  const overlayStatus = overlayStatusFromView(view)
  // Support reads this tab and never writes it (CHA1-1975 BLOCK 2.2), so their
  // view is disabled outright rather than through the merchant-side conditions.
  const formDisabled = usersViewForAdmin || !canEdit || !!overlayStatus || !view.isEditable

  // The page's single Configure read, and it is authoritative: the response
  // replaces the whole server-owned state. Re-runs per business account,
  // clearing the previous one first; the sequence number drops any response
  // that is no longer the newest.
  useEffect(() => {
    if (isDemoMode) return undefined

    const sequence = requestSequence.current + 1
    requestSequence.current = sequence

    resetConfigure()
    setLoadFailed(false)

    loadProfile(targetBaId).then((result) => {
      if (requestSequence.current !== sequence) return

      if (!result.ok) {
        // A failed load must never leave the previous account's successful
        // state on screen, and must never be mistaken for a genuinely empty
        // profile.
        setLoadFailed(true)
        return
      }

      // CHA1-2085 — the server now returns `wizard.is_awaiting_activation`
      // computed with the CC gate (get_merchant_visible_program_statuses),
      // so no client-side derivation from `applications` is needed here.
      hydrateConfigure({
        baId: targetBaId || "none",
        blocks: buildingBlocksFromConfigure(result.state),
        wizard: result.state.wizard,
        baseVersionId: result.state.baseVersionId
      })
    })

    return () => {
      // Bumping the sequence invalidates the in-flight response, so an
      // unmounted or superseded page cannot update state.
      requestSequence.current += 1
    }
  }, [isDemoMode, targetBaId, resetConfigure, hydrateConfigure])

  const validSection = SECTION_KEYS.includes(section) ? section : null

  // Unsaved-edits guard: warn before a full unload if any section is dirty.
  useEffect(() => {
    if (!hasUnsavedEdits) return undefined
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [hasUnsavedEdits])

  useEffect(() => {
    setShowErrors(false)
  }, [validSection])

  // On step change, return the form pane (and the page on mobile) to the top
  // so each step opens from its start rather than inheriting the previous
  // step's scroll position. useLayoutEffect resets before paint to avoid a
  // visible jump.
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    document.getElementById("main-content")?.scrollTo(0, 0)
  }, [validSection])

  // Demo / External Demo (§18.2): rendered for UI parity but the real wizard
  // never mounts — no loadProfile call (guarded above), no form, and no
  // redirect — the URL stays whatever the merchant navigated to.
  if (isDemoMode) {
    return (
      <div className={styles.wizard}>
        <div className={styles.main}>
          <div className={`${styles.mainScroll} ${styles.placeholder}`}>
            <Title level={4}>{t("Configure isn't available in demo mode")}</Title>
            <Text type="secondary">
              {t("This section is disabled while you're viewing the demo experience.")}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  // Nothing form-shaped renders before the first authoritative response: an
  // empty wizard would invite edits over data the server is about to send.
  if (loadFailed) {
    return (
      <div className={styles.shell}>
        {adminTitle}
        <ConfigureTabs
          activeTab={RECEIPT_CONFIGURE_TABS.PROFILE}
          usersViewForAdmin={usersViewForAdmin}
        />
        <div className={styles.placeholder}>
          <Title level={4}>{t("We couldn't load your Digital Receipt details")}</Title>
          <Text type="secondary">{t("Refresh the page to try again.")}</Text>
        </div>
      </div>
    )
  }

  if (!isHydrated) {
    return (
      <div className={styles.shell}>
        {adminTitle}
        <ConfigureTabs
          activeTab={RECEIPT_CONFIGURE_TABS.PROFILE}
          usersViewForAdmin={usersViewForAdmin}
        />
        <WizardSkeleton />
      </div>
    )
  }

  // A section-less URL (or an unknown one) means "resume where I left off":
  // sent here from the sidebar or the parent `/configure/receipts` redirect.
  // Runs after hydration so the wizard's own last-incomplete pointer is
  // available; before that the spinner is shown above.
  if (!validSection) {
    const resumeSection = view.initialStep || SECTION_KEYS[0]
    // Support resumes inside Business settings, keeping the BA query param; a
    // merchant-path redirect here would drop them out of the admin context.
    return usersViewForAdmin ? (
      <Navigate
        to={{
          pathname: RoutesPaths.receipts.admin_configure_profile_hash(resumeSection),
          search
        }}
        replace
      />
    ) : (
      <Navigate to={RoutesPaths.receipts.configure_profile_hash(resumeSection)} replace />
    )
  }

  // Configure doubles as the first-time setup wizard: a not_enrolled merchant
  // arrives here from "Enroll now" to fill in their details, and only actually
  // enrolls from the footer once every required step is complete (see Footer).

  // The live preview is hidden on the Review step (the main pane already shows
  // the finished receipt full-width). No more "summary overview" branch — a
  // finished profile now opens straight into Step 1 with the form editable /
  // read-only depending on the server's wizard flags (CHA1-2083).
  const showPreview = validSection !== RECEIPT_CONFIGURE_SLUGS.preview

  // A locked profile bounces the merchant to Business, where the lock is
  // explained. Support is read-only on every step anyway, so the lock is not a
  // reason to hide the content from them (CHA1-1975 BLOCK 2 / UC9).
  if (overlayStatus && !usersViewForAdmin) {
    return <Navigate to={RoutesPaths.receipts.configure_business} />
  }

  return (
    <div className={styles.shell}>
      {adminTitle}
      <ConfigureTabs
        activeTab={RECEIPT_CONFIGURE_TABS.PROFILE}
        usersViewForAdmin={usersViewForAdmin}
      />
      <div className={styles.wizard}>
        <LeftRail
          currentSection={validSection}
          showPreviewToggle={isMobile && showPreview}
          onOpenPreview={() => setPreviewOpen(true)}
          onRequestNext={(ok) => setShowErrors(!ok)}
          usersViewForAdmin={usersViewForAdmin}
        />
        <div className={styles.main}>
          <div className={styles.mainScroll} ref={scrollRef}>
            <SectionForm
              sectionKey={validSection}
              showErrors={showErrors}
              disabled={formDisabled}
            />
          </div>
          {/* Support gets no footer: every control on it writes (Save / Next /
              Update details), and those writes resolve against the admin's own
              session BA rather than the merchant's — CHA1-1975 BLOCK 2 asks for
              a read-only Profile with no submit. */}
          {usersViewForAdmin ? null : (
            <Footer currentSection={validSection} onRequestNext={(ok) => setShowErrors(!ok)} />
          )}
        </div>
        {showPreview && !isMobile ? <LivePreview section={validSection} /> : null}
        {!usersViewForAdmin && overlayStatus ? (
          <ProfileStatusOverlay status={overlayStatus} />
        ) : null}
      </div>
      {isMobile && showPreview ? (
        <Drawer
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          placement="right"
          width="100%"
          rootClassName={styles.previewDrawer}
          destroyOnClose
        >
          <LivePreview section={validSection} />
        </Drawer>
      ) : null}
    </div>
  )
}

const mapStateToProps = (state) => ({
  hasUnsavedEdits: Object.values(state.receiptConfigure?.ui?.dirtySections || {}).some(Boolean),
  wizard: state.receiptConfigure?.profile?.wizard,
  isHydrated: !!state.receiptConfigure?.profile?.hydratedBaId,
  baRoleId: state.auth?.userInfo?.ba_role?.id
})
const mapDispatchToProps = (dispatch) => ({
  hydrateConfigure: (payload) => dispatch(hydrateConfigureAction(payload)),
  resetConfigure: () => dispatch(resetConfigureAction())
})

export default connect(mapStateToProps, mapDispatchToProps)(ReceiptConfigurePage)
